import {
    Box,
    Button,
    CircularProgress,
    MuiIcons,
    Paper,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@leap4/frontend";
import React, { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
    useGetDashboardCatalogue,
    useGetPublisherRoster,
} from "../../../../api/generated/api";
import type { GetPublisherRosterBasis } from "../../../../api/generated/models";
import { urlService } from "../../../routing/urlsService";
import GrainStatus from "../grainStatus";
import DateRange from "../dateRange";
import PayoutGate from "../payoutGate";
import { useStickyRange } from "../useStickyRange";
import DashboardTabs from "../tabs";
import CampaignSelect, { DEFAULT_CAMPAIGN, useCampaign } from "../useCampaign";
import RosterTable, { SortKey } from "./rosterTable";

const { Storefront } = MuiIcons;

const QUERY_OPTS = {
    keepPreviousData: true,
    staleTime: 120000,
    cacheTime: 900000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
} as const;

const valueOf = (r: any, key: SortKey): number | null => {
    const v = r[key];
    return typeof v === "number" ? v : null;
};

/** Holds the scope; every figure is computed by the backend. */
const Publishers = (): JSX.Element => {
    const history = useHistory();
    const [campaign, setCampaign] = useCampaign();
    const [from, to, applyRange] = useStickyRange("publishers");
    const [basis, setBasis] = useState<string | null>(null);
    const [expanded, setExpanded] = useState<string | null>(null);
    const [sortKey, setSortKey] = useState<SortKey>("cost");
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

    const catalogue = useGetDashboardCatalogue(
        { campaign },
        { query: { ...QUERY_OPTS, staleTime: 600000 } }
    ).data;

    // Attributed by default, as in Explorer: it is the only basis that covers every publisher.
    const activeBasis = basis || "attributed";
    const mode = catalogue?.modes.find((m) => m.basis === activeBasis);

    const query = useGetPublisherRoster(
        { campaign, basis: activeBasis as GetPublisherRosterBasis, from, to },
        { query: { ...QUERY_OPTS, enabled: !!catalogue } }
    );
    const roster = query.data;

    const sorted = useMemo(() => {
        if (!roster) return null;
        const dir = sortDirection === "asc" ? 1 : -1;
        const rows = [...roster.rows].sort((a, b) => {
            const av = valueOf(a, sortKey);
            const bv = valueOf(b, sortKey);
            // Nulls last regardless of direction — an unknown is not a small number.
            if (av === null && bv === null) return 0;
            if (av === null) return 1;
            if (bv === null) return -1;
            if (!Number.isFinite(av)) return 1;
            if (!Number.isFinite(bv)) return -1;
            return (av - bv) * dir;
        });
        return { ...roster, rows };
    }, [roster, sortKey, sortDirection]);

    const onSort = useCallback(
        (key: SortKey) => {
            setSortDirection(sortKey === key && sortDirection === "desc" ? "asc" : "desc");
            setSortKey(key);
        },
        [sortKey, sortDirection]
    );

    const openWorkspace = useCallback(
        (publisher: string) => {
            const q = campaign === DEFAULT_CAMPAIGN ? "" : `?campaign=${encodeURIComponent(campaign)}`;
            history.push(`${urlService.dashboardPublisher(publisher)}${q}`);
        },
        [history, campaign]
    );

    const openInExplorer = useCallback(
        (publisher: string) => {
            const p = new URLSearchParams({
                from,
                to,
                basis: activeBasis,
                analyzeBy: "app_bundle",
                minImpressions: "0",
            });
            p.append("publisher", publisher);
            if (campaign !== DEFAULT_CAMPAIGN) p.set("campaign", campaign);
            history.push(`${urlService.dashboardExplorer()}?${p.toString()}`);
        },
        [history, from, to, activeBasis, campaign]
    );

    if (!catalogue && query.isLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Storefront color="primary" />
                    <Typography variant="h6">{roster?.axis.label ?? "Publishers"}</Typography>
                </Box>
                <DashboardTabs active="publishers" />
                <CampaignSelect campaign={campaign} onChange={setCampaign} />
            </Box>

            <PayoutGate campaign={campaign} payout={catalogue?.payoutPerConversion ?? null}>

            <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, position: "relative" }}>
                <Box sx={{ position: "absolute", top: 12, right: 12 }}>
                    <GrainStatus loading={query.isFetching} />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        gap: 1.5,
                        flexWrap: "wrap",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={activeBasis}
                        onChange={(_e: unknown, v: string | null) => v && setBasis(v)}
                    >
                        {(catalogue?.modes ?? []).map((m) => (
                            <ToggleButton key={m.basis} value={m.basis} disabled={!m.available}>
                                <Tooltip title={m.available ? "" : m.reason ?? ""} arrow>
                                    <span>{m.label}</span>
                                </Tooltip>
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>

                    <DateRange
                        from={from}
                        to={to}
                        onApply={applyRange}
                    />
                </Box>
            </Paper>

            {query.isError && (
                <Paper variant="outlined" sx={{ p: 3, mb: 1.5 }}>
                    <Typography variant="body2" color="error">
                        The roster could not be loaded.
                    </Typography>
                    <Button sx={{ mt: 1 }} size="small" variant="outlined" onClick={() => query.refetch()}>
                        Try again
                    </Button>
                </Paper>
            )}

            {sorted && (
                <>
                    <RosterTable
                        roster={sorted}
                        conversionLabel={mode?.conversionLabel ?? "Conversions"}
                        rpmLabel={mode?.rpmLabel ?? "RPM"}
                        sortKey={sortKey}
                        sortDirection={sortDirection}
                        onSort={onSort}
                        expanded={expanded}
                        onToggle={(p) => setExpanded(expanded === p ? null : p)}
                        onOpen={openWorkspace}
                        onExplore={openInExplorer}
                    />
                    <Box sx={{ display: "flex", gap: 2, px: 1, py: 1, fontSize: 12, color: "text.secondary" }}>
                        <span>Click a row for its daily history</span>
                        <span>·</span>
                        <span>Open the arrow for that {roster?.axis.label.toLowerCase()}'s own dashboard</span>
                        {roster?.scanLimited && (
                            <Box component="span" sx={{ color: "warning.main" }}>
                                · The scan hit its safety limit, so some days are missing — narrow the
                                range for a complete answer.
                            </Box>
                        )}
                    </Box>
                </>
            )}
            </PayoutGate>
        </Box>
    );
};

export default Publishers;
